import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, FlaskConical, CheckCircle2, AlertTriangle } from "lucide-react";
import { submitPeptideInquiry } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { BRAND } from "@/data/content";

const PURPOSES = [
  "Research / Lab Use",
  "General Information",
  "Other",
];

export default function PeptideInquiryModal({ open, onOpenChange, peptide }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience_level: PURPOSES[0],
    notes: "",
    acknowledged: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) setDone(false);
  }, [open]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email required.");
      return;
    }
    if (!form.acknowledged) {
      toast.error("Please acknowledge the research-use-only notice.");
      return;
    }
    setSubmitting(true);
    try {
      const { acknowledged, ...payload } = form;
      await submitPeptideInquiry({ ...payload, peptide: peptide || "General" });
      trackEvent("peptide_inquiry", {
        peptide: peptide || "General",
        purpose: form.experience_level,
      });
      setDone(true);
      toast.success("Research inquiry received.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Submission failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="peptide-modal"
        className="bg-ng-surface border border-ng-blue/40 text-white max-w-xl rounded-none p-0 max-h-[90vh] overflow-y-auto shadow-[0_0_80px_-20px_rgba(30,144,255,0.6)]"
      >
        <div className="p-8 md:p-10">
          {done ? (
            <div data-testid="peptide-success" className="text-center py-6">
              <CheckCircle2 size={48} strokeWidth={1.5} className="text-ng-blue mx-auto mb-6" />
              <DialogHeader>
                <DialogTitle className="font-display font-bold uppercase text-3xl text-white">
                  Inquiry received
                </DialogTitle>
                <DialogDescription className="text-white/65 mt-3 font-light">
                  Coach J will follow up with research-use information,
                  Certificate of Analysis details, and availability. You can also
                  text or call Coach J at{" "}
                  <span className="text-ng-blue">{BRAND.phone}</span>.
                </DialogDescription>
              </DialogHeader>
              <button
                data-testid="peptide-close"
                onClick={() => onOpenChange(false)}
                className="mt-8 inline-flex bg-ng-blue text-white px-8 py-3 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-3">
                  <FlaskConical size={16} strokeWidth={1.5} className="text-ng-blue" />
                  <div className="label-eyebrow">
                    NextGen Pro Peptides Inquiry
                  </div>
                </div>
                <DialogTitle className="font-display font-bold uppercase text-2xl md:text-3xl text-white tracking-tight">
                  Inquire: {peptide || "Peptide"}
                </DialogTitle>
                <DialogDescription className="text-white/65 mt-2 font-light">
                  Research-grade compound inquiry. For laboratory research use
                  only. Or text or call Coach J at {BRAND.phone}.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 border border-yellow-500/40 bg-yellow-500/5 p-3 flex gap-3 items-start">
                <AlertTriangle size={16} strokeWidth={1.5} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/70 text-[11px] leading-relaxed">
                  <span className="text-yellow-400 font-display font-bold uppercase">For research use only.</span>{" "}
                  Not for human or animal consumption. Not intended for medical or therapeutic use.
                </p>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-6">
                <Input
                  label="Full Name"
                  testId="peptide-name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Email"
                    type="email"
                    testId="peptide-email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    required
                  />
                  <Input
                    label="Phone"
                    testId="peptide-phone"
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                  />
                </div>

                <div>
                  <label className="label-eyebrow block mb-2">
                    Purpose
                  </label>
                  <select
                    data-testid="peptide-level"
                    value={form.experience_level}
                    onChange={(e) => update("experience_level", e.target.value)}
                    className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors font-display"
                  >
                    {PURPOSES.map((l) => (
                      <option key={l} value={l} className="bg-ng-surface">
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label-eyebrow block mb-2">
                    Additional notes
                  </label>
                  <textarea
                    data-testid="peptide-notes"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Anything else we should know?"
                    className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30 resize-none"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer text-xs text-white/70">
                  <input
                    type="checkbox"
                    data-testid="peptide-ack"
                    checked={form.acknowledged}
                    onChange={(e) => update("acknowledged", e.target.checked)}
                    className="mt-0.5 accent-ng-blue w-4 h-4"
                  />
                  <span>
                    I acknowledge that all NextGen Pro Peptides products are sold
                    for research use only, not for human or animal consumption,
                    and not intended for medical or therapeutic use.
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="peptide-submit"
                  disabled={submitting}
                  className="w-full bg-ng-blue text-white px-6 py-4 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors flex items-center justify-center gap-3 disabled:opacity-60 shadow-[0_0_40px_-8px_rgba(30,144,255,0.7)]"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>Submit Research Inquiry</>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30"
      />
    </div>
  );
}
